import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDndMonitor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useShallow } from "zustand/react/shallow";
import { AlertTriangle, CloudCheck, Download, Info, Upload } from "lucide-react";
import { strFromU8, unzipSync } from "fflate";
import MainPanel from "./components/MainPanel";
import { AppRegionErrorBoundary, ModalErrorBoundary } from "./components/ErrorBoundaryFallbacks";
import MobileLibraryDragChrome from "./components/MobileLibraryDragChrome";
import UploadIssueFlowModal from "./components/UploadIssueFlowModal";
import UrdfImportChooserModal from "./components/UrdfImportChooserModal";
import PropertiesPanelManager from "./components/property-panels/PropertiesPanelManager";
import PerformancePanel from "./components/draggable-panels/PerformancePanel";

import Scene from "./viewer/Scene.jsx";
import OnboardingOverlay from "./components/OnboardingOverlay";
import PositionGizmoLabel from "./selected-body-transform-gizmo/PositionGizmoLabel";
import RotationGizmoLabel from "./selected-body-transform-gizmo/RotationGizmoLabel";
import ViewportToolbar from "./components/viewport-toolbar/ViewportToolbar";
import CameraViewMenu from "./components/camera-view-menu/CameraViewMenu";
import { useViewerStore } from "./stores/viewerStore";
import { usePanelStore } from "./stores/panelStore";
import { useFileStore } from "./stores/fileStore";
import { getSceneState, getSceneActions, useSceneStore } from "./stores/sceneStore";
import { getBodyTree } from "./stores/sceneActions/bodyUtils";
import { useSelectionStore } from "./stores/selectionStore";
import { getHistoryActions, makeHistorySnapshot } from "./stores/historyStore";
import { getAssetActions } from "./stores/assetStore";
import { getProjectSaveActions } from "./stores/projectSaveStore";
import * as assetCache from "./runtime/assetCache";
import ShortcutProvider from "./shortcuts/ShortcutProvider";
import { SHORTCUT_ACTIONS } from "./shortcuts/defaultBindings";
import { useShortcutAction } from "./shortcuts/useShortcutAction";
import "./App.css";
import { useIsMobile } from "./hooks/useIsMobile";
import { useMobileEdgeChromeStore } from "./stores/mobileEdgeChromeStore";
import { isPhysicsAuthoringLocked } from "./utils/physicsGuards";
import { loadEnvironmentZip } from "./utils/environmentBundle";
import { isSafeRelativeArchivePath, normalizeArchivePath } from "./utils/archivePaths";
import {
  isBrowserImportLimitError,
  readZipFileWithinBrowserImportLimits,
  showBrowserImportLimitIssue,
  userMessageForImportError,
} from "./utils/importLimits";
import { logger } from "./utils/logger";
import {
  applyReducedMotionDocumentClass,
  subscribeReducedMotionPreference,
} from "./utils/reducedMotionPreference";
import {
  hasBrowserUnloadBlockingWork,
  markBeforeUnloadIfSceneWouldBeReplaced,
  requestSceneReplacementConfirmation,
} from "./utils/sceneReplacementGuards";
import { buildProjectSaveSnapshotHash } from "./utils/projectSaveSnapshot";
import { rememberDiscardedSceneForRestore } from "./utils/discardedSceneRecovery";
import {
  OPEN_RUNTIME_DIAGNOSTICS_EVENT,
  showWebGl2UnavailableToastIfNeeded,
} from "./utils/browserCapabilityToasts";
import { prepareFilesForUrdfImport } from "./utils/urdfImportChoice";

const DROPPABLE_FILE_EXTENSIONS = new Set([
  ".critter",
  ".critterrefs",
  ".zip",
  ".xml",
  ".urdf",
  ".xacro",
  ".obj",
  ".stl",
  ".dae",
  ".glb",
  ".gltf",
  ".mtl",
  ".png",
  ".jpg",
  ".jpeg",
  ".bmp",
  ".tga",
  ".webp",
  ".ktx",
  ".ktx2",
  ".dat",
  ".bin",
  ".npy",
  ".json",
]);
const MAX_NATIVE_DROP_FILES = 1500;

const hasNativeFiles = (dataTransfer) => {
  if (!dataTransfer) return false;
  const types = Array.from(dataTransfer.types || []);
  if (types.includes("Files")) return true;
  return Array.from(dataTransfer.items || []).some((item) => item?.kind === "file");
};

const SUPPORTED_FILE_MIME_TYPES = new Set([
  // Windows/native drags often expose custom extensions such as `.critter` as octet-stream
  // before the drop event provides a reliable filename.
  "application/octet-stream",
  "application/zip",
  "application/x-zip-compressed",
  "application/xml",
  "text/xml",
  "model/stl",
  "model/gltf+json",
  "model/gltf-binary",
  "image/png",
  "image/jpeg",
  "image/bmp",
  "image/tga",
  "image/webp",
  "image/ktx",
  "image/ktx2",
  "application/json",
]);

const hasDetectedDroppableSceneFile = (dataTransfer) => {
  const files = Array.from(dataTransfer?.files || []);
  if (files.length > 0) {
    return files.some((file) => isDroppableSceneFile(file));
  }
  const items = Array.from(dataTransfer?.items || []);
  if (items.length === 0 && Array.from(dataTransfer?.types || []).includes("Files")) return true;
  return items.some((item) => {
    if (item?.kind !== "file") return false;
    const file = typeof item.getAsFile === "function" ? item.getAsFile() : null;
    if (file?.name) return isDroppableSceneFile(file);
    const type = String(item.type || "").toLowerCase();
    if (!type) return true;
    return type ? SUPPORTED_FILE_MIME_TYPES.has(type) : false;
  });
};

const droppableFileName = (file, relativePath = "") => {
  const path = String(relativePath || file?.webkitRelativePath || file?.name || "");
  return path.split("/").pop() || String(file?.name || "");
};

const isDroppableSceneFile = (file, relativePath = "") => {
  const name = droppableFileName(file, relativePath).toLowerCase();
  return Array.from(DROPPABLE_FILE_EXTENSIONS).some((extension) => name.endsWith(extension));
};

const isProjectArchiveFile = (file) => {
  const name = droppableFileName(file).toLowerCase();
  return name.endsWith(".critter") || name.endsWith(".critterrefs") || name.endsWith(".zip");
};
const isXmlFile = (file) => {
  const name = droppableFileName(file).toLowerCase();
  return name.endsWith(".xml") || name.endsWith(".urdf") || name.endsWith(".xacro");
};
const isMeshFile = (file) => {
  const name = droppableFileName(file).toLowerCase();
  return (
    name.endsWith(".obj") ||
    name.endsWith(".stl") ||
    name.endsWith(".dae") ||
    name.endsWith(".glb") ||
    name.endsWith(".gltf")
  );
};

const showDropWarning = (message) => {
  window.dispatchEvent(
    new CustomEvent("critter-toast", {
      detail: { message },
    })
  );
};

const isFileStoreImportBusy = () => {
  const state = useFileStore.getState();
  return Boolean(state.uploadingFiles || state.parsingXml || state.processingMeshes);
};

const hasFileStoreSceneWork = () => {
  const state = useFileStore.getState();
  return Boolean(
    state.xmlFile ||
    Object.keys(state.fileMap || {}).length > 0 ||
    Object.keys(state.keyframes || {}).length > 0 ||
    state.currentLoadingFolder
  );
};

const getCurrentProjectSaveSnapshotHash = () =>
  buildProjectSaveSnapshotHash({
    scene: getSceneState(),
    fileState: useFileStore.getState(),
  });

const waitForFileStoreImportIdle = async (timeoutMs = 60000) => {
  await new Promise((resolve) => window.setTimeout(resolve, 0));
  if (!isFileStoreImportBusy()) return;

  await new Promise((resolve) => {
    let unsubscribe = () => {};
    const timeout = window.setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeoutMs);

    const finishIfIdle = () => {
      if (isFileStoreImportBusy()) return;
      window.clearTimeout(timeout);
      unsubscribe();
      resolve();
    };

    unsubscribe = useFileStore.subscribe(finishIfIdle);
    finishIfIdle();
  });
};

const normalizeZipPath = (path) =>
  isSafeRelativeArchivePath(path) ? normalizeArchivePath(path) : "";

const findZipEntryByBasename = (entries, basename) => {
  const lowerBasename = basename.toLowerCase();
  return Object.keys(entries || {}).find((path) => {
    const normalized = normalizeZipPath(path).toLowerCase();
    return normalized === lowerBasename || normalized.endsWith(`/${lowerBasename}`);
  });
};

const isCritterProjectArchive = async (file) => {
  try {
    const zipBytes = await readZipFileWithinBrowserImportLimits(file);
    const entries = unzipSync(zipBytes);
    const manifestPath = findZipEntryByBasename(entries, "project.json");
    if (manifestPath) {
      const manifestBytes = entries[manifestPath];
      if (!manifestBytes) return false;
      const manifest = JSON.parse(strFromU8(manifestBytes));
      return manifest?.type === "critterapp-project-manifest";
    }

    const configPath = findZipEntryByBasename(entries, "scene_config.json");
    if (!configPath) return false;

    const configBytes = entries[configPath];
    if (!configBytes) return false;
    const config = JSON.parse(strFromU8(configBytes));
    return config?.type === "critterapp-scene-config";
  } catch (error) {
    if (isBrowserImportLimitError(error)) throw error;
    logger.warn("[App] Dropped archive could not be inspected as a Critter project.", {
      fileName: file?.name,
      error,
    });
    return false;
  }
};

const withRelativePath = (file, relativePath) => {
  if (!relativePath || file?.webkitRelativePath) return file;
  try {
    Object.defineProperty(file, "webkitRelativePath", {
      value: relativePath,
      configurable: true,
    });
  } catch (error) {
    logger.warn("[App] Could not preserve dropped file relative path.", {
      fileName: file?.name,
      relativePath,
      error,
    });
  }
  return file;
};

const readDirectoryEntries = (reader) =>
  new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });

const collectFilesFromEntry = async (entry, pathPrefix, files) => {
  if (!entry || files.length >= MAX_NATIVE_DROP_FILES) return;

  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    const relativePath = `${pathPrefix}${file.name}`;
    if (isDroppableSceneFile(file, relativePath)) {
      files.push(withRelativePath(file, relativePath));
    }
    return;
  }

  if (!entry.isDirectory) return;

  const nextPrefix = `${pathPrefix}${entry.name}/`;
  const reader = entry.createReader();
  while (files.length < MAX_NATIVE_DROP_FILES) {
    const entries = await readDirectoryEntries(reader);
    if (!entries.length) break;
    for (const childEntry of entries) {
      await collectFilesFromEntry(childEntry, nextPrefix, files);
      if (files.length >= MAX_NATIVE_DROP_FILES) break;
    }
  }
};

const collectNativeDropFiles = async (dataTransfer) => {
  const items = Array.from(dataTransfer?.items || []);
  const files = [];
  let usedEntryApi = false;

  for (const item of items) {
    if (item?.kind !== "file") continue;
    const entry = typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null;
    if (!entry) continue;
    usedEntryApi = true;
    await collectFilesFromEntry(entry, "", files);
    if (files.length >= MAX_NATIVE_DROP_FILES) break;
  }

  if (usedEntryApi) return files;

  return Array.from(dataTransfer?.files || [])
    .filter((file) => isDroppableSceneFile(file))
    .slice(0, MAX_NATIVE_DROP_FILES);
};

function LibraryDragHelperLabel({ dragData }) {
  if (!dragData) return null;
  const name = dragData.previewName || "Library item";
  return (
    <div
      className="pointer-events-none inline-block rounded border border-gray-300/60 bg-gray-900/80 px-2 py-1 text-xs text-gray-100 shadow-md select-none whitespace-nowrap"
      style={{ transform: "translate(14px, 14px)" }}
    >
      {name}
    </div>
  );
}

const LibraryDragOverlay = React.memo(function LibraryDragOverlay() {
  const [dragData, setDragData] = useState(null);

  useDndMonitor({
    onDragStart: (event) => {
      const next = event?.active?.data?.current;
      const overlayOk =
        next && (next.dragSource === "objectLibrary" || next.dragSource === "terrainPanel");
      setDragData(overlayOk ? next : null);
    },
    onDragEnd: () => {
      setDragData(null);
    },
    onDragCancel: () => {
      setDragData(null);
    },
  });

  return (
    <DragOverlay dropAnimation={null}>
      <LibraryDragHelperLabel dragData={dragData} />
    </DragOverlay>
  );
});

function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const toastDismissTimersRef = useRef(new Map());
  const toastIdRef = useRef(0);

  useEffect(() => {
    const clearToastTimer = (id) => {
      const timer = toastDismissTimersRef.current.get(id);
      if (!timer) return;
      window.clearTimeout(timer);
      toastDismissTimersRef.current.delete(id);
    };
    const dismissToast = (id) => {
      clearToastTimer(id);
      setToasts((current) => current.filter((toast) => toast.id !== id));
    };
    const onToast = (event) => {
      const message = String(event?.detail?.message || "").trim();
      if (!message) return;
      if (message.toLowerCase().includes("best-effort asset integrity")) {
        window.dispatchEvent(
          new CustomEvent("critter-project-integrity", {
            detail: {
              policy: "best-effort",
              complete: false,
              reason: "missing-integrity",
            },
          })
        );
        return;
      }
      const id = `${Date.now()}-${toastIdRef.current++}`;
      const action = event?.detail?.action;
      const toast = {
        id,
        icon: ["cloud-check", "download"].includes(event?.detail?.icon) ? event.detail.icon : null,
        message,
        tone: event?.detail?.tone === "warning" ? "warning" : "info",
        action:
          action && typeof action === "object" && action.label
            ? {
                label: String(action.label),
                event: typeof action.event === "string" ? action.event : null,
              }
            : null,
      };
      setToasts((current) => [...current, toast]);
      const timer = window.setTimeout(
        () => {
          dismissToast(id);
        },
        Number(event?.detail?.durationMs) > 0 ? Number(event.detail.durationMs) : 7000
      );
      toastDismissTimersRef.current.set(id, timer);
    };
    const toastDismissTimers = toastDismissTimersRef.current;
    window.addEventListener("critter-toast", onToast);
    return () => {
      window.removeEventListener("critter-toast", onToast);
      toastDismissTimers.forEach((timer) => window.clearTimeout(timer));
      toastDismissTimers.clear();
    };
  }, []);

  const dismissToast = (id) => {
    const timer = toastDismissTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      toastDismissTimersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  };

  const handleToastAction = (toast) => {
    if (toast?.action?.event) {
      window.dispatchEvent(new CustomEvent(toast.action.event));
    }
    dismissToast(toast.id);
  };

  if (toasts.length === 0) return null;
  return (
    <div
      className="fixed left-1/2 top-4 z-[1200] flex w-[min(92vw,560px)] -translate-x-1/2 flex-col gap-2"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-100 shadow-lg backdrop-blur-sm"
          style={{ backgroundColor: "var(--panel-surface)" }}
        >
          <div className="flex items-center gap-2.5">
            <div
              className={
                toast.tone === "warning"
                  ? "shrink-0 rounded-md border border-amber-400/70 bg-amber-500/10 p-1 text-amber-300 shadow-[0_0_0_1px_rgba(251,191,36,0.12)]"
                  : "shrink-0 rounded-md border border-sky-400/70 bg-sky-500/10 p-1 text-sky-300 shadow-[0_0_0_1px_rgba(56,189,248,0.12)]"
              }
            >
              {toast.tone === "warning" ? (
                <AlertTriangle className="h-4 w-4" aria-hidden />
              ) : toast.icon === "cloud-check" ? (
                <CloudCheck className="h-4 w-4" aria-hidden />
              ) : toast.icon === "download" ? (
                <Download className="h-4 w-4" aria-hidden />
              ) : (
                <Info className="h-4 w-4" aria-hidden />
              )}
            </div>
            <div className="min-w-0 flex-1 text-gray-200">
              {toast.message}
              {toast.action ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => handleToastAction(toast)}
                    className="toast-link"
                  >
                    {toast.action.label}
                  </button>
                </>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(toast.id)}
              className="shrink-0 rounded px-1 text-gray-400 hover:bg-gray-800 hover:text-gray-100"
              aria-label="Dismiss notification"
            >
              x
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RuntimeToastActionBridge() {
  useEffect(() => {
    const openPhysicsErrorDetails = () => {
      usePanelStore.getState().actions.setPhysicsPanelOpen(true);
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent("physics-panel-focus-error"));
      }, 100);
    };
    const openRuntimeDiagnostics = () => {
      usePanelStore.getState().actions.setRuntimeDebugOpen(true);
    };
    const openSendFeedback = () => {
      usePanelStore.getState().actions.setSendFeedbackPanelOpen(true);
    };
    window.addEventListener("physics-panel-show-error", openPhysicsErrorDetails);
    window.addEventListener(OPEN_RUNTIME_DIAGNOSTICS_EVENT, openRuntimeDiagnostics);
    window.addEventListener("physics-error-send-feedback", openSendFeedback);
    return () => {
      window.removeEventListener("physics-panel-show-error", openPhysicsErrorDetails);
      window.removeEventListener(OPEN_RUNTIME_DIAGNOSTICS_EVENT, openRuntimeDiagnostics);
      window.removeEventListener("physics-error-send-feedback", openSendFeedback);
    };
  }, []);

  return null;
}

function StartupAssetRestoreBanner({ visible, onDismiss }) {
  if (!visible) return null;
  return (
    <div className="fixed left-1/2 top-4 z-[1200] w-[min(92vw,560px)] -translate-x-1/2 rounded-md border border-amber-500/70 bg-gray-950/95 px-3 py-2 text-sm text-amber-100 shadow-lg">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          Some cached assets could not be restored. You may need to re-upload mesh or texture files.
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 rounded px-1 text-amber-200/80 hover:bg-amber-900/40 hover:text-amber-100"
          aria-label="Dismiss cached assets warning"
        >
          x
        </button>
      </div>
    </div>
  );
}

function DndAppChrome({
  isMobile,
  immersiveLibraryDrag,
  showPerformancePanel,
  sensors,
  performancePanelPosition,
  mainPanelPosition,
  showAssetRestoreWarning,
  onDismissAssetRestoreWarning,
}) {
  const hideChromeForLibraryDrag = isMobile && immersiveLibraryDrag;
  const [nativeDropActive, setNativeDropActive] = useState(false);
  const [nativeDropValid, setNativeDropValid] = useState(false);
  const [nativeDropLoading, setNativeDropLoading] = useState(false);
  const [nativeDropLoadingMessage, setNativeDropLoadingMessage] = useState("");
  const nativeDragDepthRef = useRef(0);

  const resetNativeDropState = useCallback(() => {
    nativeDragDepthRef.current = 0;
    setNativeDropActive(false);
    setNativeDropValid(false);
  }, []);

  const handleNativeDragEnter = useCallback((event) => {
    if (!hasNativeFiles(event.dataTransfer)) return;
    event.preventDefault();
    nativeDragDepthRef.current += 1;
    setNativeDropActive(true);
    setNativeDropValid(hasDetectedDroppableSceneFile(event.dataTransfer));
  }, []);

  const handleNativeDragOver = useCallback((event) => {
    if (!hasNativeFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = isPhysicsAuthoringLocked() ? "none" : "copy";
    setNativeDropActive(true);
    setNativeDropValid(hasDetectedDroppableSceneFile(event.dataTransfer));
  }, []);

  const handleNativeDragLeave = useCallback((event) => {
    if (!hasNativeFiles(event.dataTransfer)) return;
    nativeDragDepthRef.current = Math.max(0, nativeDragDepthRef.current - 1);
    if (nativeDragDepthRef.current === 0) setNativeDropActive(false);
  }, []);

  const handleNativeDrop = useCallback(
    async (event) => {
      if (!hasNativeFiles(event.dataTransfer)) return;
      event.preventDefault();
      const { dataTransfer } = event;
      resetNativeDropState();

      if (isPhysicsAuthoringLocked()) return;

      try {
        const files = await collectNativeDropFiles(dataTransfer);
        if (files.length === 0) {
          showDropWarning(
            "Drop a Critter project, MJCF/URDF XML, assets, or a folder containing them."
          );
          return;
        }
        const projectArchiveFiles = files.filter(isProjectArchiveFile);
        if (projectArchiveFiles.length > 0) {
          if (projectArchiveFiles.length > 1 || files.length > 1) {
            showDropWarning("Drop one Critter project archive at a time.");
            return;
          }

          const projectArchiveFile = projectArchiveFiles[0];
          if (!(await isCritterProjectArchive(projectArchiveFile))) {
            showDropWarning("That archive does not look like a Critter project save.");
            return;
          }
          if (
            !(await requestSceneReplacementConfirmation({
              hasFileStoreWork: hasFileStoreSceneWork(),
              currentSnapshotHash: getCurrentProjectSaveSnapshotHash(),
            }))
          )
            return;

          setNativeDropLoadingMessage("Loading Critter project...");
          setNativeDropLoading(true);
          rememberDiscardedSceneForRestore();
          await loadEnvironmentZip(projectArchiveFile);
          getProjectSaveActions().markProjectSaved(getCurrentProjectSaveSnapshotHash(), "import");
          getHistoryActions().clearHistory();
          return;
        }
        const preparedFiles = await prepareFilesForUrdfImport(files);
        if (!preparedFiles) return;

        const fileState = useFileStore.getState();
        const isResolvingMissingFiles =
          Array.isArray(fileState.missingFiles) &&
          fileState.missingFiles.length > 0 &&
          !!fileState.xmlFile;
        const canStartImport = preparedFiles.some(isXmlFile) || preparedFiles.some(isMeshFile);
        if (!isResolvingMissingFiles && !canStartImport) {
          showDropWarning(
            "Drop a Critter project, MJCF/URDF XML, OBJ/STL/DAE/glTF mesh, or files needed by a pending import."
          );
          return;
        }
        if (preparedFiles.length >= MAX_NATIVE_DROP_FILES) {
          showDropWarning(
            `Only the first ${MAX_NATIVE_DROP_FILES} supported dropped files were imported.`
          );
        }
        if (
          !isResolvingMissingFiles &&
          preparedFiles.some(isXmlFile) &&
          !(await requestSceneReplacementConfirmation({
            hasFileStoreWork: hasFileStoreSceneWork(),
            currentSnapshotHash: getCurrentProjectSaveSnapshotHash(),
          }))
        )
          return;
        setNativeDropLoadingMessage("Importing scene files...");
        setNativeDropLoading(true);
        if (!isResolvingMissingFiles && preparedFiles.some(isXmlFile)) {
          rememberDiscardedSceneForRestore();
        }
        await useFileStore.getState().actions.handleFiles({
          target: {
            files: preparedFiles,
            value: "",
          },
        });
        await waitForFileStoreImportIdle();
        if (!isResolvingMissingFiles && preparedFiles.some(isXmlFile)) {
          getProjectSaveActions().markProjectSaved(getCurrentProjectSaveSnapshotHash(), "import");
          getHistoryActions().clearHistory();
        }
      } catch (error) {
        logger.error("[App] Failed to import dropped files:", error);
        if (showBrowserImportLimitIssue(error)) return;
        showDropWarning(
          userMessageForImportError(
            error,
            "Dropped files could not be imported. Check the console for details."
          )
        );
      } finally {
        setNativeDropLoading(false);
        setNativeDropLoadingMessage("");
      }
    },
    [resetNativeDropState]
  );

  return (
    <DndContext sensors={sensors}>
      <div
        onDragEnter={handleNativeDragEnter}
        onDragOver={handleNativeDragOver}
        onDragLeave={handleNativeDragLeave}
        onDrop={handleNativeDrop}
        className="relative h-screen w-screen overflow-hidden"
      >
        <AppRegionErrorBoundary name="scene" title="Scene viewport">
          <Scene />
        </AppRegionErrorBoundary>
        <StartupAssetRestoreBanner
          visible={showAssetRestoreWarning}
          onDismiss={onDismissAssetRestoreWarning}
        />
        {(nativeDropActive || nativeDropLoading) && (
          <div className="native-file-drop-overlay">
            <div
              className={`native-file-drop-overlay__panel${
                nativeDropValid && !nativeDropLoading
                  ? " native-file-drop-overlay__panel--valid"
                  : ""
              }`}
            >
              {nativeDropLoading ? (
                <div className="native-file-drop-overlay__spinner" aria-hidden="true" />
              ) : (
                <Upload className="native-file-drop-overlay__icon" aria-hidden="true" />
              )}
              <div className="native-file-drop-overlay__title">
                {nativeDropLoading
                  ? nativeDropLoadingMessage || "Importing scene..."
                  : "Drop scene files to import"}
              </div>
              <div className="native-file-drop-overlay__body">
                {nativeDropLoading
                  ? "This can take a moment for large scenes or asset bundles."
                  : ".critter and .critterrefs project files, XML/URDF files, assets, or folders can be dropped into the scene."}
              </div>
            </div>
          </div>
        )}
        <div
          className={hideChromeForLibraryDrag ? "mobile-hide-chrome-for-library-drag" : undefined}
          inert={hideChromeForLibraryDrag ? true : undefined}
        >
          <OnboardingOverlay />

          <AppRegionErrorBoundary name="main-panel" title="Main panel">
            <MainPanel position={mainPanelPosition} />
          </AppRegionErrorBoundary>

          <AppRegionErrorBoundary name="properties-panel" title="Inspector">
            <PropertiesPanelManager />
          </AppRegionErrorBoundary>

          <ViewportToolbar />

          <CameraViewMenu />

          {showPerformancePanel && <PerformancePanel position={performancePanelPosition} />}

          <PositionGizmoLabel />
          <RotationGizmoLabel />
          <ModalErrorBoundary name="upload-issue-flow-modal" title="Upload issue flow dialog">
            <UploadIssueFlowModal />
          </ModalErrorBoundary>
          <ModalErrorBoundary name="urdf-import-chooser-modal" title="URDF import chooser dialog">
            <UrdfImportChooserModal />
          </ModalErrorBoundary>
          <RuntimeToastActionBridge />
          <ToastHost />
        </div>
        <MobileLibraryDragChrome />
      </div>
      <LibraryDragOverlay />
    </DndContext>
  );
}

const MemoDndAppChrome = React.memo(DndAppChrome);

function App() {
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 16 },
  });
  // Touch-first: installs iOS-required non-passive `touchmove` hook.
  // Keep activation distance-based so a normal press-and-drag works on mobile.
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { distance: 16 },
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(touchSensor, pointerSensor, keyboardSensor);
  const isMobile = useIsMobile();
  const [assetRestoreWarning, setAssetRestoreWarning] = useState(false);

  useEffect(() => {
    showWebGl2UnavailableToastIfNeeded();
  }, []);
  const immersiveLibraryDrag = useMobileEdgeChromeStore((s) => s.immersiveLibraryDragActive);
  // Only get what we actually use from stores
  const { showPerformancePanel } = useViewerStore(
    useShallow((state) => ({
      showPerformancePanel: state.showPerformancePanel,
    }))
  );

  // Track whether we've saved the initial history entry for the current load
  const initialHistorySavedRef = useRef(false);
  const previousXmlKeyRef = useRef("");
  const pendingXmlParseKeyRef = useRef(null);

  useEffect(() => {
    applyReducedMotionDocumentClass();
    return subscribeReducedMotionPreference(applyReducedMotionDocumentClass);
  }, []);

  // On mount, load presets from JSON file and ensure clean initial state
  useEffect(() => {
    useFileStore.getState().actions.loadPresets();
    // Rehydrate asset manifest from previous session
    try {
      void getAssetActions()
        .rehydrateFromPersisted()
        .catch((error) => {
          logger.warn("[App] Failed to rehydrate asset store from persisted data", error);
          setAssetRestoreWarning(true);
        });
    } catch (error) {
      logger.warn("[App] Failed to rehydrate asset store from persisted data", error);
      setAssetRestoreWarning(true);
    }
    // Load built-in texture catalog for Material Editor dropdown options
    import("./utils/builtinTextures").then((mod) => {
      try {
        mod.loadBuiltinTexturesCatalog();
      } catch (error) {
        logger.warn("[App] Failed to load built-in textures catalog", error);
      }
    });

    // Ensure the scene is cleared on initial load for a fresh start
    // This makes the initial state equivalent to clicking "Start Empty Project"
    const selectionActions = useSelectionStore.getState();
    const panelActions = usePanelStore.getState().actions;

    // Clear any existing state to ensure a clean start
    selectionActions.clearAllSelections();
    selectionActions.clearAllHovers();
    panelActions.setExpandedBodies({});
    getSceneActions().clearJoints();

    // Reset initial history gate on full reset
    initialHistorySavedRef.current = false;
  }, []);

  // Revoke any blob URLs and dispose cached resources on unload
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const shouldBlockUnload = hasBrowserUnloadBlockingWork(getCurrentProjectSaveSnapshotHash());
      if (shouldBlockUnload) {
        markBeforeUnloadIfSceneWouldBeReplaced(event, shouldBlockUnload);
        return;
      }
      try {
        assetCache.disposeAll?.();
      } catch (error) {
        logger.warn("[App] Failed to dispose cached assets on unload", error);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Parse loaded XML once files are ready without subscribing App render to fileStore.
  useEffect(() => {
    const maybeParseLoadedXml = () => {
      const { xmlFile, fileMap, missingFiles, presets, currentLoadingFolder } =
        useFileStore.getState();
      const xmlKey = xmlFile ? `${xmlFile.name}:${xmlFile.size}:${xmlFile.lastModified}` : "";

      if (xmlKey !== previousXmlKeyRef.current) {
        initialHistorySavedRef.current = false;
        previousXmlKeyRef.current = xmlKey;
        pendingXmlParseKeyRef.current = null;
      }

      if (!xmlFile) return;
      if (!fileMap || Object.keys(fileMap).length === 0) return;
      if (Array.isArray(missingFiles) && missingFiles.length > 0) return;
      if (pendingXmlParseKeyRef.current === xmlKey) return;

      pendingXmlParseKeyRef.current = xmlKey;
      const presetName =
        currentLoadingFolder && presets?.[currentLoadingFolder]?.name
          ? presets[currentLoadingFolder].name
          : undefined;

      void useFileStore
        .getState()
        .actions.parseXmlFile(xmlFile, fileMap, { worldBodyNameBase: presetName })
        .finally(() => {
          if (pendingXmlParseKeyRef.current === xmlKey) {
            pendingXmlParseKeyRef.current = null;
          }
        });
    };

    maybeParseLoadedXml();
    const unsubscribe = useFileStore.subscribe((state, previousState) => {
      if (
        state.xmlFile === previousState.xmlFile &&
        state.fileMap === previousState.fileMap &&
        state.missingFiles === previousState.missingFiles &&
        state.presets === previousState.presets &&
        state.currentLoadingFolder === previousState.currentLoadingFolder
      ) {
        return;
      }
      maybeParseLoadedXml();
    });
    return unsubscribe;
  }, []);

  // When bodies are set, expand all bodies by default
  useEffect(() => {
    const maybeExpandBodiesOnce = () => {
      if (initialHistorySavedRef.current) return;
      const { bodies, worldBodies } = getSceneState();
      if (!bodies || Object.keys(bodies).length === 0) return;
      const bodyTree = getBodyTree({ bodies, worldBodies });
      usePanelStore.getState().actions.expandAllBodies(bodyTree);
      initialHistorySavedRef.current = true;
    };

    maybeExpandBodiesOnce();
    const unsubscribe = useSceneStore.subscribe((state, previousState) => {
      if (
        state.bodies === previousState.bodies &&
        state.worldBodies === previousState.worldBodies
      ) {
        return;
      }
      maybeExpandBodiesOnce();
    });
    return unsubscribe;
  }, []);

  const mainPanelPosition = useMemo(
    () => ({
      top: 10,
      left: 10,
      zIndex: 20,
    }),
    []
  );
  const performancePanelPosition = useMemo(
    () => ({
      bottom: 20,
      right: 20,
      zIndex: 30,
    }),
    []
  );
  const handleShortcutUndo = useCallback(() => {
    if (isPhysicsAuthoringLocked()) return true;
    const historyActions = getHistoryActions();
    if (historyActions.canUndo()) {
      try {
        historyActions.undo(
          () => makeHistorySnapshot(getSceneState()),
          (snapshot) => {
            const robotActions = getSceneActions();
            robotActions.restoreState(snapshot.scene);
            useFileStore.setState({ keyframes: snapshot.keyframes || {} });
          }
        );
      } catch (error) {
        logger.error("[App] Shortcut undo failed:", error);
      }
    }
    return true;
  }, []);

  const handleShortcutRedo = useCallback(() => {
    if (isPhysicsAuthoringLocked()) return true;
    const historyActions = getHistoryActions();
    if (historyActions.canRedo()) {
      try {
        historyActions.redo(
          () => makeHistorySnapshot(getSceneState()),
          (snapshot) => {
            const robotActions = getSceneActions();
            robotActions.restoreState(snapshot.scene);
            useFileStore.setState({ keyframes: snapshot.keyframes || {} });
          }
        );
      } catch (error) {
        logger.error("[App] Shortcut redo failed:", error);
      }
    }
    return true;
  }, []);

  const historyShortcutOptions = useMemo(
    () => ({
      allowInEditable: true,
      preventDefault: true,
    }),
    []
  );

  useShortcutAction(SHORTCUT_ACTIONS.HISTORY_UNDO, handleShortcutUndo, historyShortcutOptions);
  useShortcutAction(SHORTCUT_ACTIONS.HISTORY_REDO, handleShortcutRedo, historyShortcutOptions);

  return (
    <ShortcutProvider>
      <MemoDndAppChrome
        isMobile={isMobile}
        immersiveLibraryDrag={immersiveLibraryDrag}
        showPerformancePanel={showPerformancePanel}
        sensors={sensors}
        performancePanelPosition={performancePanelPosition}
        mainPanelPosition={mainPanelPosition}
        showAssetRestoreWarning={assetRestoreWarning}
        onDismissAssetRestoreWarning={() => setAssetRestoreWarning(false)}
      />
    </ShortcutProvider>
  );
}

export default App;
